import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Download, ExternalLink, KeyRound, ShieldAlert, Vault, Palette, Send, XCircle, Loader2, ArrowLeftRight, MessageSquareWarning } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FILE = '/okibank/OceansBank.html';
const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEPS = [
  'Click "Download Program" on the escape room computer and save it to the Desktop.',
  'Double-click OceansBank.html. It opens in the browser and works offline (live hints need internet).',
  'Press "Full Screen" on the login screen. It locks: leaving full screen needs the admin password. Use Chrome or Edge for the strongest lock (Esc must be held, Alt+Tab and the Windows key are blocked).',
  'Open admin with the faint ⚙ in the bottom-left corner (or Ctrl+Shift+A) to edit everything.',
];

const DEFAULTS = [
  { icon: KeyRound, label: 'Player login', value: 'manager / shisa77' },
  { icon: ShieldAlert, label: 'Admin password', value: 'okiadmin' },
  { icon: Vault, label: 'Vault hint codes 1-7', value: '0930 · 0714 · 0451 · SHISA · 1250 · MIYAGI · 2468' },
  { icon: ArrowLeftRight, label: 'Transfer puzzle', value: '¥1,738,000 Shisa → Kenji → RYUKYU-88' },
  { icon: MessageSquareWarning, label: 'Offline hint key', value: 'Ctrl+Shift+H' },
  { icon: Palette, label: 'Themes', value: 'Terminal · Modern · Retro Windows' },
];

const QUICK = [
  'Check every transaction carefully.',
  'Some transactions are only visible inside the account.',
  'The vault code hides in a safe deposit fee.',
  'A pending wire is waiting for someone to authorise it.',
];

const download = async () => {
  try {
    const r = await fetch(FILE);
    const html = (await r.text()).replace('__HINT_SERVER__', API);
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'OceansBank.html';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch { toast.error('Download failed'); }
};

function HintSender() {
  const [room, setRoom] = useState('main');
  const [text, setText] = useState('');
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/bank/hint`, { params: { room } });
      setCurrent(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [room]);

  const send = async (msg) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/bank/hint`, { room, text: msg }, { headers: authHeaders() });
      setCurrent(r.data);
      toast.success(msg ? 'Hint sent to the bank screen' : 'Hint cleared');
      if (msg) setText('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send hint'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border-2 border-amber-500/40 bg-card p-5 space-y-3" data-testid="bank-hint-sender">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading text-xs tracking-widest uppercase flex items-center gap-2">
          <MessageSquareWarning size={14} className="text-amber-400" /> Send a Live Hint
        </h3>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Room
          <input value={room} onChange={e => setRoom(e.target.value)} data-testid="bank-hint-room"
            className="w-28 bg-background border border-border rounded-lg px-2 py-1 text-xs font-mono outline-none focus:border-amber-500" />
        </label>
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={3} data-testid="bank-hint-input"
        placeholder="Type a hint. It pops up on the bank screen within a few seconds."
        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500" />
      <div className="flex flex-wrap gap-2">
        {QUICK.map((q, i) => (
          <button key={q} type="button" onClick={() => setText(q)} data-testid={`bank-hint-quick-${i}`}
            className="text-[11px] border border-amber-500/30 text-amber-200 hover:bg-amber-900/30 rounded-full px-3 py-1 transition-colors">{q}</button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <button type="button" onClick={() => send(text.trim())} disabled={busy || !text.trim()} data-testid="bank-hint-send"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send Hint
        </button>
        <button type="button" onClick={() => send('')} disabled={busy || !current?.text} data-testid="bank-hint-clear"
          className="inline-flex items-center gap-2 border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full">
          <XCircle size={13} /> Clear From Screen
        </button>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="bank-hint-current">
          On screen now: {current?.text ? <b className="text-amber-200">{current.text}</b> : <i>nothing</i>}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        The room computer needs internet, and the room name must match the one in the program's admin (Transfer & Hints). No internet? Press Ctrl+Shift+H on the room computer instead.
      </p>
    </div>
  );
}

export default function BankProgram() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="bank-program-section">
      <div className="rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 via-black to-neutral-950 p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <Landmark className="text-emerald-400 shrink-0" size={34} />
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-2xl tracking-wider uppercase">Oceans Bank</h2>
            <p className="text-sm text-muted-foreground mt-1">
              A fake banking program for the escape room: loans, accounts, interest margins, a password login with a lockout alarm, hidden clues, a funds-transfer puzzle, a vault puzzle and live game-master hints. English and Japanese.
            </p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap mt-5">
          <button type="button" onClick={download} data-testid="bank-program-download"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-black font-heading text-xs tracking-widest uppercase px-5 py-3 rounded-full transition-colors">
            <Download size={14} /> Download Program
          </button>
          <a href={FILE} target="_blank" rel="noreferrer" data-testid="bank-program-preview"
            className="inline-flex items-center gap-2 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/30 font-heading text-xs tracking-widest uppercase px-5 py-3 rounded-full transition-colors">
            <ExternalLink size={14} /> Try It Here
          </a>
        </div>
      </div>

      <HintSender />

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="bank-program-steps">
          <h3 className="font-heading text-xs tracking-widest uppercase mb-3">How to set it up</h3>
          <ol className="space-y-2 text-sm list-decimal list-inside text-muted-foreground">
            {STEPS.map(s => <li key={s}>{s}</li>)}
          </ol>
        </div>
        <div className="rounded-xl border border-border bg-card p-5" data-testid="bank-program-defaults">
          <h3 className="font-heading text-xs tracking-widest uppercase mb-3">Starting setup (change it in the program's admin)</h3>
          <ul className="space-y-2.5">
            {DEFAULTS.map(d => (
              <li key={d.label} className="flex items-center gap-2 text-sm flex-wrap">
                <d.icon size={14} className="text-emerald-400 shrink-0" />
                <span className="text-muted-foreground">{d.label}:</span>
                <span className="font-mono text-foreground">{d.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Data is saved on each computer separately. Use the program's Backup tab to export or import data between computers.
      </p>
    </div>
  );
}
